import { Play, Pause, Music2, Clock, Flame } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { usePlayer, fmtTime } from '@/context/PlayerContext';
import { LikeButton, ShareButton } from '@/components/actions';

const FALLBACK = 'https://images.pexels.com/photos/167092/pexels-photo-167092.jpeg?auto=compress&cs=tinysrgb&w=600';

function statusBadge(status) {
  const map = {
    completed: ['Prête', 'text-[#22C55E] bg-[#22C55E]/10'],
    generating: ['Génération...', 'text-[#EAB308] bg-[#EAB308]/10'],
    queued: ['En file', 'text-[#3B82F6] bg-[#3B82F6]/10'],
    failed: ['Échec', 'text-[#EF4444] bg-[#EF4444]/10'],
    draft: ['Brouillon', 'text-muted-foreground bg-white/5'],
  };
  return map[status] || map.draft;
}

export function StyleCard({ style, onClick }) {
  return (
    <button
      onClick={onClick}
      data-testid={`style-card-${style.slug}`}
      className="card-surface card-hover group relative aspect-square overflow-hidden text-left"
    >
      <img src={style.cover_url || FALLBACK} alt={style.name} loading="lazy"
           className="absolute inset-0 h-full w-full object-cover opacity-60 transition-transform duration-500 group-hover:scale-110" />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 p-4">
        <span className="mb-1 inline-flex items-center gap-1 rounded-full bg-brand/20 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-brand">
          <Music2 className="h-3 w-3" /> Style
        </span>
        <h3 className="font-display text-base font-bold leading-tight text-white">{style.name}</h3>
      </div>
      <span className="absolute right-3 top-3 flex h-9 w-9 translate-y-2 items-center justify-center rounded-full bg-brand text-black opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100 btn-glow">
        <Play className="h-4 w-4 fill-black" />
      </span>
    </button>
  );
}

export function MusicCard({ song, queue, actions }) {
  const { playTrack, current, playing, toggle } = usePlayer();
  const isCurrent = current?.song_id === song.id;
  const track = {
    song_id: song.id, title: song.title, slug: song.slug, cover_url: song.cover_url,
    audio_url: song.audio_url, style_text: song.style_text, duration_seconds: song.duration_seconds,
    creator: song.creator?.display_name,
  };
  const [label, cls] = statusBadge(song.generation_status);
  const canPlay = song.generation_status === 'completed' && song.audio_url;

  return (
    <div className="card-surface card-hover flex w-full min-w-0 items-center gap-3 overflow-hidden p-3" data-testid={`music-card-${song.id}`}>
      <div className="relative h-16 w-16 flex-shrink-0 overflow-hidden rounded-xl">
        <img src={song.cover_url || FALLBACK} alt={song.title} loading="lazy" className="h-full w-full object-cover" />
        {canPlay && (
          <button
            onClick={() => (isCurrent ? toggle() : playTrack(track, queue))}
            data-testid={`play-song-${song.id}`}
            className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 transition-opacity hover:opacity-100"
          >
            {isCurrent && playing ? <Pause className="h-6 w-6 text-white" /> : <Play className="h-6 w-6 fill-white text-white" />}
          </button>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-2">
          <h4 className="min-w-0 truncate font-display text-base font-semibold text-white">{song.title}</h4>
          <span className={`flex-shrink-0 rounded-full px-2 py-0.5 font-mono text-[10px] uppercase ${cls}`}>{label}</span>
        </div>
        <p className="truncate text-sm text-muted-foreground">{song.style_text || 'Style libre'}</p>
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-xs text-muted-foreground">
          {song.duration_seconds ? <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{fmtTime(song.duration_seconds)}</span> : null}
          <span className={song.visibility === 'public' ? 'text-brand' : ''}>{song.visibility === 'public' ? 'Public' : 'Privé'}</span>
        </div>
      </div>
      {actions && <div className="flex-shrink-0">{actions}</div>}
    </div>
  );
}

export function ChallengeCard({ challenge, onClick }) {
  return (
    <button onClick={onClick} data-testid={`challenge-card-${challenge.slug}`}
            className="card-surface card-hover group relative flex h-48 flex-col justify-end overflow-hidden p-5 text-left">
      <img src={challenge.cover_url || FALLBACK} alt={challenge.title} loading="lazy"
           className="absolute inset-0 h-full w-full object-cover opacity-40 transition-transform duration-500 group-hover:scale-105" />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
      <div className="relative">
        <span className="mb-2 inline-flex items-center gap-1 rounded-full bg-white/10 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-white">
          {challenge.status === 'active' ? <><Flame className="h-3 w-3 text-brand" /> En cours</> : challenge.status}
        </span>
        <h3 className="font-display text-xl font-bold text-white">{challenge.emoji} {challenge.title}</h3>
        <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{challenge.description}</p>
        <p className="mt-2 font-mono text-xs text-brand">{challenge.entries_count ?? 0} participations</p>
      </div>
    </button>
  );
}
